import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle, XCircle, ArrowRight, RotateCcw } from "lucide-react";
import ProductCTA from "@/components/ProductCTA";

const ResultDisplay = ({ result, onShowLeadForm, onShowProductPage, onReset }) => {
  const getStatusIcon = () => {
    switch (result.legal_status) {
      case "safe":
        return <CheckCircle className="w-20 h-20 text-green-400" />;
      case "border":
        return <AlertTriangle className="w-20 h-20 text-yellow-400" />;
      case "illegal":
        return <XCircle className="w-20 h-20 text-red-500" />;
      case "critical":
        return <XCircle className="w-24 h-24 text-rose-600 animate-pulse" />;
      default:
        return <AlertTriangle className="w-20 h-20 text-gray-400" />;
    }
  };

  const getStatusColor = () => {
    switch (result.legal_status) {
      case "safe":
        return "from-green-500 to-emerald-600";
      case "border":
        return "from-yellow-500 to-orange-500";
      case "illegal":
        return "from-red-600 to-rose-700";
      case "critical":
        return "from-rose-700 via-red-800 to-black";
      default:
        return "from-gray-500 to-gray-600";
    }
  };

  const getBgColor = () => {
    switch (result.legal_status) {
      case "safe":
        return "bg-green-500/10 border-green-500/30";
      case "border":
        return "bg-yellow-500/10 border-yellow-500/30";
      case "illegal":
        return "bg-red-600/20 border-red-500/40";
      case "critical":
        return "bg-rose-900/30 border-rose-600/60";
      default:
        return "bg-gray-500/10 border-gray-500/30";
    }
  };

  const getGlowColor = () => {
    switch (result.legal_status) {
      case "safe":
        return "0 0 40px rgba(34, 197, 94, 0.6)";
      case "border":
        return "0 0 40px rgba(234, 179, 8, 0.6)";
      case "illegal":
        return "0 0 40px rgba(220, 38, 38, 0.8)";
      case "critical":
        return "0 0 60px rgba(159, 18, 57, 1), 0 0 90px rgba(127, 29, 29, 0.6)";
      default:
        return "0 0 40px rgba(107, 114, 128, 0.6)";
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Result Card */}
      <div className="glass-card-premium rounded-3xl p-10 text-center neon-border hover-lift" style={{boxShadow: getGlowColor()}}>
        <div className="flex justify-center mb-8 pulse-glow">{getStatusIcon()}</div>

        <h3 className="text-4xl font-black mb-6 text-gradient" data-testid="result-title">
          Promil Sonucunuz
        </h3>

        <div className="my-10">
          <div
            className={`inline-block px-12 py-6 rounded-3xl bg-gradient-to-r ${getStatusColor()} text-white shadow-2xl neon-glow`}
          >
            <p className="text-sm font-bold mb-2 uppercase tracking-wider">Güncel Promil</p>
            <p className="text-6xl font-black" data-testid="current-bac-value">
              {result.current_bac}‰
            </p>
          </div>
        </div>

        <div className="space-y-5 text-left">
          <div className={`p-6 rounded-2xl border-2 ${getBgColor()} backdrop-blur-sm`}>
            <p className="font-bold text-white mb-3 text-lg" data-testid="warning-message">
              {result.warning_message}
            </p>
            {result.penalty_info && (
              <div className="text-sm text-gray-200 font-medium leading-relaxed whitespace-pre-line" data-testid="penalty-info">
                {result.penalty_info}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-5">
            <div className="bg-gray-900/50 p-4 sm:p-6 rounded-2xl border-2 border-teal-500/20 hover:border-teal-500/40 smooth-transition">
              <p className="text-teal-400 mb-1 sm:mb-2 text-xs sm:text-sm font-bold uppercase">Ham Promil</p>
              <p className="text-2xl sm:text-4xl font-black text-white break-words" data-testid="raw-bac-value">
                {result.raw_bac}‰
              </p>
            </div>
            <div className="bg-gray-900/50 p-4 sm:p-6 rounded-2xl border-2 border-teal-500/20 hover:border-teal-500/40 smooth-transition">
              <p className="text-teal-400 mb-1 sm:mb-2 text-xs sm:text-sm font-bold uppercase">Saf Alkol</p>
              <p className="text-2xl sm:text-4xl font-black text-white break-words" data-testid="pure-alcohol-value">
                {result.pure_alcohol_grams}g
              </p>
            </div>
          </div>

          <div className="bg-gray-900/30 p-4 rounded-xl border border-gray-700 text-center">
            <p className="text-xs text-gray-400 font-medium">
              ⚠️ Bu hesaplama teoriktir; resmi ölçüm yerine geçmez. 
              Mevzuat bilgileri yalnızca kullanıcı bilgilendirme amaçlıdır; hukuki tavsiye niteliği taşımaz.
            </p>
          </div>
        </div>
      </div>

      {/* Product CTA - Önce */}
      <ProductCTA bacLevel={result.current_bac} legalStatus={result.legal_status} onShowProductPage={onShowProductPage} />

      {/* Call to Action */}
      <div className="glass-card-premium rounded-3xl p-6 text-center neon-border">
        <p className="text-gray-300 mb-6 text-base font-medium">
          Ya da önce bilgilerinizi kaydedin, size özel tekliflerimizden haberdar olun
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <Button
            onClick={onShowLeadForm}
            variant="outline"
            className="rounded-2xl px-8 py-5 font-bold text-lg bg-gray-900/50 border-2 border-teal-500/30 text-teal-400 hover:border-teal-500 hover:bg-teal-500/10"
            data-testid="show-lead-form-btn"
          >
            Bilgilerimi Kaydet
            <ArrowRight className="w-5 h-5 ml-3" />
          </Button>
          <Button
            onClick={onReset}
            variant="outline"
            className="rounded-2xl px-6 py-5 font-semibold text-base bg-gray-900/30 border border-gray-700 text-gray-400 hover:border-gray-600 hover:bg-gray-800/50"
            data-testid="reset-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Yeni Hesaplama
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ResultDisplay;
